import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../services/authService';
import { selectCurrentToken } from '../../../redux/authSlice';
import { 
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import LeadSelectionInterface from '../../../components/LeadSelectionInterface';
import LeadListSelectionInterface from '../../../components/LeadListSelectionInterface';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';

const LeadSelectionPage = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [participantsError, setParticipantsError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);

  // Form state with default values
  const [formData, setFormData] = useState({
    auto_enroll_new_leads: false,
    auto_enroll_filters: [],
    lead_filters: [],
    selected_lead_lists: [],
    total_leads_from_lists: 0
  });

  // Load campaign data on component mount
  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const response = await api.get(`/acs/nurturing-campaigns/${campaignId}/`);
        setCampaign(response.data);
        
        // Initialize form data with campaign's existing filters
        setFormData({
          auto_enroll_new_leads: response.data.auto_enroll_new_leads || false,
          auto_enroll_filters: response.data.auto_enroll_filters || [],
          lead_filters: response.data.auto_enroll_filters || [], // Initialize lead_filters with auto_enroll_filters
          selected_lead_lists: response.data.selected_lead_lists || [],
          total_leads_from_lists: response.data.total_leads_from_lists || 0
        });

        // If there are existing filters, trigger the lead count update
        if (response.data.auto_enroll_filters && response.data.auto_enroll_filters.length > 0) {
          handleFiltersChange(
            response.data.auto_enroll_filters,
            [], // We'll get the leads from the LeadSelectionInterface
            0  // We'll get the count from the LeadSelectionInterface
          );
        }
      } catch (err) {
        setError('Failed to load campaign data');
        console.error('Error loading campaign data:', err);
      }
    };
    fetchCampaignData();
  }, [campaignId]);

  // Fetch participants when campaign data is loaded
  useEffect(() => {
    if (campaign?.account && campaignId) {
      fetchParticipants();
    }
  }, [campaign?.account, campaignId]);

  const fetchParticipants = async () => {
    setLoadingParticipants(true);
    setParticipantsError(null);
    try {
      const response = await api.get('/acs/lead-nurturing-participants/', {
        params: {
          campaign: campaignId,
          account: campaign.account
        }
      });
      setParticipants(response.data);
    } catch (err) {
      setParticipantsError('Failed to load campaign participants');
      console.error('Error loading participants:', err);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate that at least one selection method is used
      if (formData.lead_filters.length === 0 && formData.selected_lead_lists.length === 0) {
        setError('Please select leads using filters or lead lists');
        return;
      }

      // Update campaign with only the necessary lead selection data
      await api.post(`/acs/nurturing-campaigns/${campaignId}/update_auto_enroll/`, {
        id: campaign.id,
        name: campaign.name,
        channel: campaign.channel,
        campaign_type: campaign.campaign_type,
        account: campaign.account,
        crm_campaign: campaign.crm_campaign,
        auto_enroll_new_leads: formData.auto_enroll_new_leads,
        auto_enroll_filters: formData.lead_filters.map(filter => ({
          ...filter,
          combine_with: filter.combine_with || 'AND' // Ensure combine_with is always set
        })),
        lead_filters: formData.lead_filters.map(filter => ({
          ...filter,
          combine_with: filter.combine_with || 'AND' // Ensure combine_with is always set
        })),
        updated_by: token.user_id
      });

      // Assign leads to the campaign
      const assignPayload = {
        status: 'active'
      };

      // Add filters if present
      if (formData.lead_filters.length > 0) {
        assignPayload.filters = formData.lead_filters.map(filter => ({
          ...filter,
          combine_with: filter.combine_with || 'AND' // Ensure combine_with is always set
        }));
      }

      // Add lead list IDs if present
      if (formData.selected_lead_lists.length > 0) {
        assignPayload.lead_list_ids = formData.selected_lead_lists.map(list => list.id);
      }

      await api.post(`/acs/nurturing-campaigns/${campaignId}/assign_leads/`, assignPayload);

      setSuccess(true);
      setTimeout(() => {
        navigate('/crm/lead-nurturing');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update lead selection');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      completed: 'info',
      paused: 'warning',
      failed: 'error',
      pending: 'default'
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleDeactivateParticipant = async (participantId) => {
    try {
      await api.post(`/acs/lead-nurturing-participants/${participantId}/update_status/`, {
        status: 'paused'
      });
      
      // Update the participant in the local state
      setParticipants(prevParticipants =>
        prevParticipants.map(p =>
          p.id === participantId ? { ...p, status: 'paused' } : p
        )
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deactivating participant:', error);
      setError(error.response?.data?.error || 'Failed to deactivate participant');
    }
  };

  const handleActivateParticipant = async (participantId) => {
    try {
      await api.post(`/acs/lead-nurturing-participants/${participantId}/update_status/`, {
        status: 'active'
      });
      
      // Update the participant in the local state
      setParticipants(prevParticipants =>
        prevParticipants.map(p =>
          p.id === participantId ? { ...p, status: 'active' } : p
        )
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error activating participant:', error);
      setError(error.response?.data?.error || 'Failed to activate participant');
    }
  };

  const handleDeleteClick = (participantId) => {
    setParticipantToDelete(participantId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!participantToDelete) return;

    try {
      await api.delete(`/acs/lead-nurturing-participants/${participantToDelete}/`);
      
      // Remove the participant from the local state
      setParticipants(prevParticipants =>
        prevParticipants.filter(p => p.id !== participantToDelete)
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting participant:', error);
      setError(error.response?.data?.error || 'Failed to delete participant');
    } finally {
      setDeleteDialogOpen(false);
      setParticipantToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setParticipantToDelete(null);
  };

  const handleFiltersChange = useCallback((filters, leads, count) => {
    setFormData(prev => ({
      ...prev,
      lead_filters: filters || [],
      auto_enroll_filters: prev.auto_enroll_new_leads ? (filters || []) : []
    }));
    setSelectedLeads(leads || []);
    setTotalLeads(count || 0);
  }, []);

  const handleLeadListsChange = useCallback((selectedLists, totalLeadsFromLists) => {
    setFormData(prev => ({
      ...prev,
      selected_lead_lists: selectedLists || [],
      total_leads_from_lists: totalLeadsFromLists || 0
    }));
  }, []);

  if (!campaign) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  const renderLeadPreview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Auto-Enrollment Configuration
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_enroll_new_leads}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    auto_enroll_new_leads: e.target.checked,
                    auto_enroll_filters: e.target.checked ? prev.lead_filters : []
                  }));
                }}
              />
            }
            label="Automatically enroll new leads that match the criteria"
          />
          {formData.auto_enroll_new_leads && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              New leads will be automatically enrolled in this campaign if they match the filters defined below.
            </Typography>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Lead Selection Filters
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Create filters to select leads based on their properties. This can be combined with lead list selection.
          </Typography>
          <LeadSelectionInterface
            accountId={campaign.account}
            campaignId={campaign.crm_campaign}
            nurturingCampaignId={campaignId}
            onFiltersChange={handleFiltersChange}
            participantsCount={participants.length}
            loadingParticipants={loadingParticipants}
            initialFilters={formData.lead_filters}
            initialAutoEnroll={formData.auto_enroll_new_leads}
          />
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              Selected Leads Preview
            </Typography>
            <Tooltip title="This preview shows the leads that will be enrolled in the campaign based on the current filters">
              <IconButton>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {(!formData.lead_filters || formData.lead_filters.length === 0) ? (
            <Alert severity="info">
              Add filters above to see a preview of the selected leads
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone Number</TableCell>
                      <TableCell>Funnel Step</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!selectedLeads || selectedLeads.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No leads match the current filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedLeads.map((lead) => (
                        <TableRow key={lead.id}>
                          <TableCell>{lead.first_name} {lead.last_name}</TableCell>
                          <TableCell>{lead.email}</TableCell>
                          <TableCell>{lead.phone_number}</TableCell>
                          <TableCell>
                            <Chip
                              label={lead.current_step_name}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={lead.status_name}
                              size="small"
                              color={lead.status_name === 'active' ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Showing {selectedLeads?.length || 0} of {totalLeads || 0} leads
              </Typography>
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );

  const renderLeadListSelection = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Lead List Assignment
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select from available lead lists to assign leads to this nurturing campaign. You can combine this with filter-based selection.
          </Typography>
          <LeadListSelectionInterface
            accountId={campaign.account}
            campaignId={campaign.crm_campaign}
            nurturingCampaignId={campaignId}
            onLeadListsChange={handleLeadListsChange}
            initialSelectedLists={formData.selected_lead_lists}
          />
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              Lead List Summary
            </Typography>
            <Tooltip title="This shows the total leads that will be assigned from selected lead lists">
              <IconButton>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {formData.selected_lead_lists.length === 0 ? (
            <Alert severity="info">
              No lead lists selected. Select lists above to see a summary.
            </Alert>
          ) : (
            <Box>
              <Typography variant="h4" color="primary" gutterBottom>
                {formData.total_leads_from_lists}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                leads will be assigned from {formData.selected_lead_lists.length} selected list(s)
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Lists:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                     {formData.selected_lead_lists.map((list) => (
                     <Chip
                       key={list.id}
                       label={`${list.name} (${list.lead_count || 0} leads)`}
                       size="small"
                       variant="outlined"
                     />
                   ))}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );

  const renderParticipants = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Campaign Participants</Typography>
            <Button
              variant="outlined"
              onClick={fetchParticipants}
              disabled={loadingParticipants}
              startIcon={loadingParticipants ? <CircularProgress size={20} /> : null}
            >
              Refresh
            </Button>
          </Box>

          {participantsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {participantsError}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lead</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Entered At</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Current Step</TableCell>
                  <TableCell>Messages Sent</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingParticipants ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : participants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No participants found
                    </TableCell>
                  </TableRow>
                ) : (
                  participants.map((participant) => (
                    <TableRow key={participant.id}>
                      <TableCell>
                        {participant?.lead_name || participant?.lead_email || participant?.lead_phone_number || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {participant?.lead_phone_number || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={participant.status}
                          color={getStatusColor(participant.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(participant.entered_campaign_at)}</TableCell>
                      <TableCell>{formatDate(participant.updated_at)}</TableCell>
                      <TableCell>
                        {participant.current_journey_step || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {participant.messages_sent_count || 0}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {participant.status === 'active' ? (
                            <Tooltip title="Deactivate Participant">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleDeactivateParticipant(participant.id)}
                              >
                                <PauseIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Activate Participant">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleActivateParticipant(participant.id)}
                              >
                                <PlayArrowIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Participant">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(participant.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header with back button and info icon */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate(-1)} 
            sx={{ mr: 2 }}
            aria-label="Go back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Lead Selection
          </Typography>
        </Box>
        
        <Tooltip title="Lead Selection Capabilities Guide">
          <IconButton 
            onClick={() => setGuideOpen(true)}
            color="primary"
            aria-label="Open filter guide"
          >
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Lead selection updated successfully! {formData.lead_filters.length > 0 && formData.selected_lead_lists.length > 0 && 'Both filters and lead lists were applied.'}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* Summary Section */}
        {(formData.lead_filters.length > 0 || formData.selected_lead_lists.length > 0) && (
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Lead Selection Summary
            </Typography>
            <Grid container spacing={2}>
              {formData.lead_filters.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">Filter-Based:</Typography>
                    <Typography variant="h6" color="primary">{totalLeads}</Typography>
                    <Typography variant="body2" color="textSecondary">leads</Typography>
                  </Box>
                </Grid>
              )}
              {formData.selected_lead_lists.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">Lead Lists:</Typography>
                    <Typography variant="h6" color="primary">{formData.total_leads_from_lists}</Typography>
                    <Typography variant="body2" color="textSecondary">leads</Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Total leads to be assigned: <strong>{totalLeads + formData.total_leads_from_lists}</strong>
                  {formData.lead_filters.length > 0 && formData.selected_lead_lists.length > 0 && 
                    ' (duplicates will be automatically removed)'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Filter-Based Selection" />
            <Tab label="Lead List Selection" />
            <Tab label="Current Participants" />
          </Tabs>
        </Box>

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && renderLeadPreview()}
          {activeTab === 1 && renderLeadListSelection()}
          {activeTab === 2 && renderParticipants()}
        </Box>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/crm/lead-nurturing')}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || (formData.lead_filters.length === 0 && formData.selected_lead_lists.length === 0)}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Lead Selection'}
          </Button>
        </Box>

        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Campaign Participant"
          message="Are you sure you want to remove this participant from the campaign? This action cannot be undone."
        />
      </Paper>

      {/* Filter Guide Side Panel */}
      <Drawer
        anchor="right"
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 600,
            maxWidth: '90vw',
            p: 3,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Lead Selection Capabilities Guide
          </Typography>
          <IconButton onClick={() => setGuideOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflow: 'auto', height: 'calc(100vh - 120px)' }}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Our advanced lead selection system allows you to create precise targeting criteria using multiple data models, field types, and operators. Build complex filters to reach exactly the right audience for your campaigns.
          </Typography>
          
          {/* Data Models Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            📊 Data Models
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Lead Model
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Core lead information including contact details, status, source, and basic demographics.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip label="Basic Info" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  <Chip label="Contact Info" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  <Chip label="Status & Classification" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  D2C Lead Model
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Direct-to-consumer specific fields including age, demographics, and consumer preferences.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip label="Age & Demographics" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  <Chip label="Consumer Data" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  <Chip label="Personal Info" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  B2B Lead Model
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Business-to-business specific fields including company information and professional details.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip label="Company Info" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  <Chip label="Professional Data" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                  <Chip label="Business Metrics" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Field Types Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            🔧 Field Types & Operators
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Text Fields</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="Equals" size="small" variant="outlined" />
                <Chip label="Contains" size="small" variant="outlined" />
                <Chip label="Does Not Contain" size="small" variant="outlined" />
                <Chip label="Starts With" size="small" variant="outlined" />
                <Chip label="Does Not Start With" size="small" variant="outlined" />
                <Chip label="Ends With" size="small" variant="outlined" />
                <Chip label="Does Not End With" size="small" variant="outlined" />
                <Chip label="In" size="small" variant="outlined" />
                <Chip label="Not In" size="small" variant="outlined" />
                <Chip label="Is Empty" size="small" variant="outlined" />
                <Chip label="Is Not Empty" size="small" variant="outlined" />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Use for names, emails, phone numbers, addresses, and text-based fields
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Numeric Fields</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="Equals" size="small" variant="outlined" />
                <Chip label="Greater Than" size="small" variant="outlined" />
                <Chip label="Less Than" size="small" variant="outlined" />
                <Chip label="Greater Than or Equal" size="small" variant="outlined" />
                <Chip label="Less Than or Equal" size="small" variant="outlined" />
                <Chip label="In" size="small" variant="outlined" />
                <Chip label="Not In" size="small" variant="outlined" />
                <Chip label="Is Empty" size="small" variant="outlined" />
                <Chip label="Is Not Empty" size="small" variant="outlined" />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Use for scores, ages, amounts, counts, and numeric metrics
              </Typography>
            </Grid>
          </Grid>

          {/* Enhanced Operators Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            🚀 Enhanced Operators
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="success.main">
                📋 List-Based Filtering
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Use <strong>In</strong> and <strong>Not In</strong> operators to match multiple values at once.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="In: Active, Pending, Qualified" size="small" variant="outlined" />
                <Chip label="Not In: Disqualified, Dead" size="small" variant="outlined" />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Enter comma-separated values: "value1, value2, value3"
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="warning.main">
                ❌ Negative Operators
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Use negative operators to exclude specific patterns or values.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="Does Not Contain: test" size="small" variant="outlined" />
                <Chip label="Does Not Start With: admin" size="small" variant="outlined" />
                <Chip label="Does Not End With: .com" size="small" variant="outlined" />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Perfect for excluding test data, admin accounts, or specific domains
              </Typography>
            </Grid>
          </Grid>

          {/* Date Filtering Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            📅 Advanced Date Filtering
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Date fields support both absolute dates and relative expressions for dynamic, time-based targeting.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Absolute Dates</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Select specific dates and times for precise targeting.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label="2024-01-15 14:30" size="small" variant="outlined" />
                <Chip label="2024-06-21" size="small" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Relative Dates</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Use dynamic expressions that automatically adjust based on current date.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label="120 days ago" size="small" variant="outlined" />
                <Chip label="last week" size="small" variant="outlined" />
                <Chip label="this month" size="small" variant="outlined" />
              </Box>
            </Grid>
          </Grid>

          {/* Value Formatting Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            📝 Value Formatting
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Single Values</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Enter a single value for exact matching.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label="Active" size="small" variant="outlined" />
                <Chip label="john@example.com" size="small" variant="outlined" />
                <Chip label="80" size="small" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>List Values</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Use comma-separated values for multiple matching.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label="Active, Pending, Qualified" size="small" variant="outlined" />
                <Chip label="10, 20, 30" size="small" variant="outlined" />
                <Chip label="2024-01-01, 2024-01-15" size="small" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Mixed Date Values</Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Combine absolute and relative dates in lists.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label="2024-01-01, 30 days ago" size="small" variant="outlined" />
                <Chip label="last week, this month" size="small" variant="outlined" />
              </Box>
            </Grid>
          </Grid>

          {/* Advanced Features Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            ⚡ Advanced Features
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom color="success.main">
                🔄 Auto-Enrollment
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Automatically enroll new leads that match your criteria as they enter your system.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom color="info.main">
                🔗 Complex Logic
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Combine multiple conditions with AND/OR logic for sophisticated targeting.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom color="warning.main">
                📊 Real-time Preview
              </Typography>
              <Typography variant="body2" color="textSecondary">
                See exactly which leads match your criteria before saving the campaign.
              </Typography>
            </Grid>
          </Grid>

          {/* Use Cases Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            🎯 Common Use Cases
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Re-engagement Campaigns</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li>Leads not contacted in 120+ days</li>
                <li>High-value leads who went cold</li>
                <li>Leads who opened emails but didn't respond</li>
                <li>Leads with status "Inactive" or "Cold"</li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>New Lead Nurturing</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li>Leads created in the last 30 days</li>
                <li>Leads with no contact history</li>
                <li>High-scoring recent leads</li>
                <li>Leads with status "New" or "Qualified"</li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Demographic Targeting</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li>Age-based campaigns (18-35, 65+)</li>
                <li>Geographic targeting by state/city</li>
                <li>Lead source-based campaigns</li>
                <li>Exclude test data and admin accounts</li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Behavioral Targeting</Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li>Leads in specific funnel steps</li>
                <li>Leads with certain status values</li>
                <li>Leads with custom field values</li>
                <li>Leads who haven't engaged recently</li>
              </Box>
            </Grid>
          </Grid>

          {/* Enhanced Use Cases Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            🚀 Advanced Use Cases with Enhanced Operators
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="success.main">
                Multi-Status Campaigns
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Target leads with multiple statuses using list operators.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="Status In: Active, Pending, Qualified" size="small" variant="outlined" />
                <Chip label="Score >= 80" size="small" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="warning.main">
                Exclusion Campaigns
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Exclude specific statuses or patterns using negative operators.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="Status Not In: Disqualified, Dead" size="small" variant="outlined" />
                <Chip label="Email Does Not Contain: test" size="small" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="info.main">
                Domain-Specific Targeting
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Target or exclude specific email domains.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="Email Ends With: @gmail.com" size="small" variant="outlined" />
                <Chip label="Email Does Not End With: @company.com" size="small" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom color="primary.main">
                Score Range Targeting
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Target leads within specific score ranges.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                <Chip label="Score In: 80, 90, 100" size="small" variant="outlined" />
                <Chip label="Score >= 70" size="small" variant="outlined" />
              </Box>
            </Grid>
          </Grid>

          {/* Tips Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            💡 Pro Tips
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2 }}>
                <li>Start with broad filters and narrow down gradually</li>
                <li>Use relative dates for campaigns that need to stay current</li>
                <li>Test your filters with the preview before saving</li>
                <li>Combine multiple conditions for precise targeting</li>
                <li>Use list operators instead of multiple OR conditions</li>
                <li>Use negative operators to exclude test data and admin accounts</li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2 }}>
                <li>Use auto-enrollment for ongoing campaigns</li>
                <li>Monitor participant counts to optimize filters</li>
                <li>Consider lead scoring for high-value targeting</li>
                <li>Regularly review and update filter criteria</li>
                <li>Use "In" and "Not In" for cleaner, more efficient filters</li>
                <li>Combine absolute and relative dates in list values</li>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    </Container>
  );
};

export default LeadSelectionPage; 